import React, { useState } from 'react';
import { Button, Card, Col, Container, Row, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull, isUndefined, map, startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faDownload, faLock, faPencil } from '@fortawesome/free-solid-svg-icons';
import { selectIsCustomer, selectIsFrontline, selectIsInsurer } from '@components/Auth';
import { Loading, CopyLinkButton, LoadingError, RowItem, ConfirmModal } from '@components/Base';
import { OrderStatuses, dOrderPeriod } from '@models/Order';
import { OrderBadge } from '@components/Feature/Order';
import { dUserName } from '@models/User';
import { GeneralQuoteDescriptionCardBody } from '@components/Feature/GeneralQuote';
import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { InsurancePolicyUserCardBody } from '@components/Feature/InsurancePolicyUser';
import { QuotePremiumCardBody } from '@components/Feature/Quote';
import { IngestionCardBody } from '@components/Feature/Ingestion';

import { useInsurancePolicyFetch } from '../hook';
import { routeInsurancePolicyBuyEditPage, routeInsurancePolicyBuyPayPage } from '../route';
import {
  insurancePolicyLock,
  InsurancePolicyLockPayload,
  insurancePolicyPayByCash,
  InsurancePolicyPayByCashPayload,
} from '../action';
import { IngestionTypes } from '@models/Ingestion';

const ROW_ITEM_PROPS = {
  className: 'mb-2',
};

const InsurancePolicyBuyReviewPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isFrontline = useSelector(selectIsFrontline);
  const isCustomer = useSelector(selectIsCustomer);
  const isInsurer = useSelector(selectIsInsurer);
  const policy = useInsurancePolicyFetch();
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showPayByCashDialog, setShowPayByCashDialog] = useState(false);

  if (isUndefined(policy)) return <Loading />;
  if (isNull(policy)) return <LoadingError />;

  const {
    id,
    status,
    policyNumber,
    startedAt,
    endedAt,
    proof,
    note,
    frontlineUser,
    descriptionHtml,
    company,
    insuranceType,
    policyType,
    primaryInsurancePolicyUser,
  } = policy;

  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isActive = status === OrderStatuses.Active;
  const shouldEdit = isFrontline && (isPending || isLocked || isActive);
  const shouldLock = isFrontline && isPending;
  const shouldPay = isCustomer && isLocked && policyType === PolicyTypes.Personal;
  const shouldPayByCash = isFrontline && isLocked && policyType === PolicyTypes.Commercial;

  return (
    <>
      <Container className="my-3">
        <Row className="gy-5">
          <Col lg={8}>
            <Card>
              <Card.Header className="d-flex flex-row justify-content-between align-items-center">
                {t('common:summary')}
                <CopyLinkButton />
              </Card.Header>
              <Card.Body>
                <Card.Title>{t('common:policyDetails')}</Card.Title>
                <RowItem {...ROW_ITEM_PROPS} label={t('common:policyNumber')} value={policyNumber} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:theInsuredPolicyholder')} value={primaryInsurancePolicyUser?.name || company?.name || ''} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:periodOfInsurance')} value={dOrderPeriod(startedAt, endedAt)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:insuranceType')} value={tInsuranceTypeEnum(insuranceType)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:policyType')} value={tPolicyType(policyType)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:status')} value={<OrderBadge status={status} />} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:frontlineUser')} value={dUserName(frontlineUser)} />

                {(isFrontline || isInsurer) && (
                  <>
                    <RowItem
                      {...ROW_ITEM_PROPS}
                      label={t('common:proof')}
                      value={(
                        <a href={proof} target="_blank" rel="noreferrer noopener" >
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          {t('proof')}
                        </a>
                      )}
                    />

                    <RowItem {...ROW_ITEM_PROPS} label={t('common:note')} value={note} />
                  </>
                )}
              </Card.Body>

              <GeneralQuoteDescriptionCardBody descriptionHtml={descriptionHtml} />
              <IngestionCardBody ingestableType='InsurancePolicy' ingestableId={policy.id} ingestionTypes={[IngestionTypes.InsurancePolicyUserAdd]} />
              <InsurancePolicyUserCardBody {...policy} />
            </Card>
          </Col>

          <Col lg>
            <Card className="mb-4">
              <QuotePremiumCardBody {...policy} planName={map(policy.benefitTemplates, 'name').join(', ')} />

              <Card.Body>
                <Stack direction="vertical" gap={2}>
                  {shouldEdit && (
                    <Button as={Link} to={routeInsurancePolicyBuyEditPage({ insurancePolicyId: id })} variant="outline-primary">
                      <FontAwesomeIcon icon={faPencil} className="me-2" />
                      {t('common:edit')}
                    </Button>
                  )}

                  {shouldLock && (
                    <Button onClick={() => setShowLockDialog(true)} variant="warning">
                      <FontAwesomeIcon icon={faLock} className="me-2" />
                      {t('common:confirm')}
                    </Button>
                  )}

                  {shouldPay && (
                    <Button as={Link} to={routeInsurancePolicyBuyPayPage({ insurancePolicyId: id })}>
                      <span className="me-2">{startCase(t('common:payNow'))}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  )}

                  {shouldPayByCash && (
                    <Button onClick={() => setShowPayByCashDialog(true)}>
                      <span className="me-2">{startCase(t('common:payByCash'))}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  )}
                </Stack>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {shouldLock && (
        <ConfirmModal
          show={showLockDialog}
          onHide={() => setShowLockDialog(false)}
          onConfirm={() => {
            const payload: InsurancePolicyLockPayload = { id };
            dispatch(insurancePolicyLock(payload));
          }}
        />
      )}

      {shouldPayByCash && (
        <ConfirmModal
          show={showPayByCashDialog}
          onHide={() => setShowPayByCashDialog(false)}
          onConfirm={() => {
            const payload: InsurancePolicyPayByCashPayload = { id };
            dispatch(insurancePolicyPayByCash(payload));
          }}
        />
      )}
    </>
  );
};

export { InsurancePolicyBuyReviewPage };
